import React, { useEffect } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../hooks/use-toast';
import CartSizeSelector from './CartSizeSelector';
import { checkIsWorkingHours, getWorkingHoursMessage } from '../utils/workingHours';

/* Formatting: "350.850,48" → { int: "350.850", dec: ",48" } */
const splitPrice = (num) => {
    const str = Number(num || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const idx = str.lastIndexOf(',');
    return { int: str.slice(0, idx), dec: str.slice(idx) };
};

const CartDrawer = () => {
    const { cart, removeFromCart, updateCartQuantity, updateCartItemSize, cartTotal, activeCartTotal, paymentMethod, setPaymentMethod, isCartOpen, setIsCartOpen } = useCart();
    const navigate = useNavigate();

    const { toast } = useToast();

    // Body scroll yönetimi
    useEffect(() => {
        if (isCartOpen) {
            document.body.style.overflow = 'hidden';
            if (!checkIsWorkingHours() && paymentMethod === 'HAVALE') {
                setPaymentMethod('CREDIT_CARD');
            }
        } else {
            document.body.style.overflow = 'auto';
        }
        
        // Cleanup: component unmount olduğunda scroll'u geri aç
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isCartOpen]);

    const handleCheckout = () => {
        if (paymentMethod === 'HAVALE' && !checkIsWorkingHours()) {
            toast({
                title: 'Sipariş Verilemiyor',
                description: getWorkingHoursMessage(),
                variant: 'destructive',
            });
            return;
        }
        setIsCartOpen(false);
        navigate('/sepet');
    };

    const handleCloseDrawer = () => {
        setIsCartOpen(false);
    };

    if (!isCartOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[1000] transition-all duration-300"
                onClick={handleCloseDrawer}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full md:w-[450px] bg-white z-[1002] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-[#D4AF37]/20">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-[#D4AF37]/10 bg-[#FAF9F6]">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 shrink-0 bg-white rounded-full flex items-center justify-center border border-[#D4AF37]/20 shadow-sm">
                            <ShoppingBag className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                        <div>
                            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a]">Sepetim</h2>
                            <p className="text-sm text-[#8A8A8A] font-medium">{cart.length} ürün</p>
                        </div>
                    </div>
                    <button
                        onClick={handleCloseDrawer}
                        aria-label="Sepeti kapat"
                        className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-white hover:shadow-md transition-all text-[#5A5A5A] hover:text-[#D4AF37]"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto p-6 bg-white">
                    {cart.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                            <div className="w-24 h-24 rounded-full bg-[#FAF9F6] flex items-center justify-center border border-[#D4AF37]/10 relative">
                                <div className="absolute inset-0 rounded-full border border-[#D4AF37]/20 animate-pulse"></div>
                                <ShoppingBag className="w-10 h-10 text-[#D4AF37]" />
                            </div>
                            <div>
                                <p className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">Sepetiniz Boş</p>
                                <p className="text-[#8A8A8A]">Henüz sepetinize ürün eklemediniz.</p>
                            </div>
                            <button
                                onClick={() => setIsCartOpen(false)}
                                className="group relative px-8 py-3 bg-[#1a1a1a] text-white overflow-hidden rounded-lg transition-all hover:bg-[#D4AF37] hover:shadow-lg hover:shadow-[#D4AF37]/20"
                            >
                                <span className="relative flex items-center gap-2 font-medium">
                                    Alışverişe Başla
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {cart.map((item) => (
                                <div key={item.cartKey || item.id} className="cart-item-container group relative bg-[#FAF9F6] rounded-xl p-4 border border-transparent hover:border-[#D4AF37]/20 transition-all hover:shadow-md" style={{ zIndex: 1 }}>
                                    <div className="flex gap-4">
                                        {/* Product Image */}
                                        <div className="w-24 h-24 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-[#E8E8E8]">
                                            <img
                                                src={item.images?.[0] || item.image || 'https://api.carmengold.com/uploads/products/default.png'}
                                                alt={item.name}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                                            <div>
                                                <h4 className="font-jost text-[1.2rem] sm:text-[1.3rem] font-semibold text-gray-900 transition-all duration-300 group-hover:text-animated-gold group-hover:underline decoration-[#D4AF37] decoration-[1.5px] underline-offset-[3px] leading-snug tracking-wide" style={{ fontFamily: "'Jost', sans-serif" }}>
                                                    {item.name}
                                                </h4>
                                                

                                                
                                                {(item.availableSizes?.options?.length > 0 || item.sizes?.options?.length > 0) && (
                                                    <div className="flex items-center gap-2 mt-1 mb-1">
                                                        <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                                                            {item.sizeLabel || item.sizes?.label || item.availableSizes?.label || 'Ölçü'}:
                                                        </p>
                                                        <CartSizeSelector item={item} updateCartItemSize={updateCartItemSize} />
                                                    </div>
                                                )}
                                                
                                                {/* Eski fiyat (çizili) - sadece indirim varsa */}
                                                {item.originalPrice && item.discountActive && (
                                                    <div className="text-gray-500 line-through text-xs font-medium mt-1">
                                                        {Number(item.originalPrice * item.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                                    </div>
                                                )}
                                                
                                                {/* İndirimli fiyat ve rozet yan yana */}
                                                <div className="flex items-center gap-3 mt-1">
                                                    <div className="text-[#D4AF37] font-bold text-[1.2rem] sm:text-[1.3rem] font-jost tracking-tight leading-none flex items-baseline gap-0.5">
                                                        {(() => {
                                                            const itemUnitPrice = paymentMethod === 'HAVALE' 
                                                                ? (item.havalePrice || item.basePrice || item.price || 0) 
                                                                : (item.creditCardPrice || (item.basePrice ? Number(item.basePrice) * 1.05 : Number(item.price || 0) * 1.05));
                                                            const { int, dec } = splitPrice(Number(itemUnitPrice) * item.quantity);
                                                            return (
                                                                <>
                                                                    <span>{int}</span>
                                                                    <span className="text-[0.85rem] sm:text-[0.95rem]">{dec}</span>
                                                                </>
                                                            );
                                                        })()}
                                                        <span className="text-[1.1rem] sm:text-[1.2rem] text-[#b8941f] font-bold ml-1 font-jost">₺</span>
                                                    </div>
                                                    {item.discountActive && item.discountPercentage && parseFloat(item.discountPercentage) > 0 && (
                                                        <div
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[20px] text-[0.65rem] font-bold tracking-wider"
                                                            style={{ background: '#1a1a1a', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', fontFamily: "'Jost',sans-serif" }}
                                                        >
                                                            %{Math.round(parseFloat(item.discountPercentage))} İNDİRİM
                                                        </div>
                                                    )}
                                                </div>
                                                {item.stockQuantity <= 0 && (
                                                    <p className="text-[10px] text-red-500 font-bold uppercase mt-1">Stokta Yok</p>
                                                )}
                                            </div>

                                            {/* Controls */}
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-[#E8E8E8] shadow-sm">
                                                    {/* Min 44px touch target for mobile */}
                                                    <button
                                                        className="w-11 h-11 flex items-center justify-center hover:bg-[#FAF9F6] rounded transition-colors"
                                                        onClick={() => updateCartQuantity(item.cartKey || item.id, Math.max(1, item.quantity - 1))}
                                                        aria-label="Azalt"
                                                    >
                                                        <Minus className="h-3 w-3 text-[#5A5A5A]" />
                                                    </button>
                                                    <span className="w-7 text-center text-sm font-semibold text-[#1a1a1a]">{item.quantity}</span>
                                                    <button
                                                        className="w-11 h-11 flex items-center justify-center hover:bg-[#FAF9F6] rounded transition-colors"
                                                        onClick={() => updateCartQuantity(item.cartKey || item.id, item.quantity + 1)}
                                                        aria-label="Artır"
                                                    >
                                                        <Plus className="h-3 w-3 text-[#5A5A5A]" />
                                                    </button>
                                                </div>

                                                <button
                                                    className="w-11 h-11 flex items-center justify-center rounded-full text-red-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                                    onClick={() => removeFromCart(item.cartKey || item.id)}
                                                    aria-label="Ürünü kaldır"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {cart.length > 0 && (
                    <div className="border-t border-[#D4AF37]/10 p-6 bg-[#FAF9F6] space-y-4">
                        {/* Ödeme Yöntemi Seçici */}
                        <div className="flex items-center justify-between p-1.5 bg-white rounded-xl border border-gray-200 gap-1.5 mb-2 shadow-xs">
                            <button
                                type="button"
                                onClick={() => setPaymentMethod('CREDIT_CARD')}
                                className={`flex-1 py-2 px-2 rounded-lg text-xs font-bold transition-all text-center ${
                                    paymentMethod === 'CREDIT_CARD'
                                        ? 'bg-[#1a1a1a] text-[#D4AF37] shadow-sm'
                                        : 'text-gray-500 hover:text-gray-900'
                                }`}
                            >
                                💳 Kredi Kartı
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!checkIsWorkingHours()) {
                                        toast({
                                            title: 'Havale Ödemesi Kapalı',
                                            description: getWorkingHoursMessage(),
                                            variant: 'destructive',
                                        });
                                        return;
                                    }
                                    setPaymentMethod('HAVALE');
                                }}
                                className={`flex-1 py-2 px-2 rounded-lg text-xs font-bold transition-all text-center ${
                                    !checkIsWorkingHours()
                                        ? 'bg-gray-100 text-gray-400 opacity-70 border border-gray-200 cursor-not-allowed'
                                        : paymentMethod === 'HAVALE'
                                            ? 'bg-pink-700 text-white shadow-sm'
                                            : 'text-gray-500 hover:text-pink-700'
                                }`}
                                title={!checkIsWorkingHours() ? getWorkingHoursMessage() : ''}
                            >
                                {checkIsWorkingHours() ? '🏦 Havale / EFT' : '⏰ Havale'}
                            </button>
                        </div>

                        <div className="space-y-2">
                             <div className="flex justify-between items-center text-[#5A5A5A]">
                                <span>Ara Toplam</span>
                                <span className="font-jost flex items-baseline gap-0.5">
                                    {(() => {
                                        const { int, dec } = splitPrice(activeCartTotal);
                                        return (
                                            <>
                                                <span>{int}</span>
                                                <span className="text-[0.85rem]">{dec}</span>
                                            </>
                                        );
                                    })()}
                                    <span className="text-[#b8941f] font-bold ml-1">₺</span>
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-[#5A5A5A]">
                                <span>Kargo</span>
                                <span className="text-green-600 font-medium text-xs bg-green-50 px-2 py-1 rounded-full">ÜCRETSİZ</span>
                            </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-dashed border-[#D4AF37]/20">
                            <span className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a]">Toplam</span>
                            <div className="text-[#D4AF37] font-bold text-2xl font-jost tracking-tight flex items-baseline gap-0.5">
                                {(() => {
                                    const { int, dec } = splitPrice(activeCartTotal);
                                    return (
                                        <>
                                            <span>{int}</span>
                                            <span className="text-[1.1rem]">{dec}</span>
                                        </>
                                    );
                                })()}
                                <span className="text-[1.1rem] sm:text-[1.2rem] text-[#b8941f] font-bold ml-1 font-jost">₺</span>
                            </div>
                        </div>

                        <button
                            onClick={handleCheckout}
                            className="w-full h-14 bg-[#1a1a1a] text-white font-medium text-lg rounded-xl hover:bg-[#D4AF37] shadow-lg hover:shadow-[#D4AF37]/20 transition-all duration-300 group overflow-hidden relative"
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                Sepete Git
                                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CartDrawer;
